import { mediaService } from "Apis";
import { IUploaderProps, S3Response } from "features/media/types";
import React, { useRef, useCallback, useImperativeHandle, forwardRef } from "react";


export interface UploaderRef {
  uploadFile: (file: File) => Promise<void>;
}

const Uploader = forwardRef<UploaderRef, IUploaderProps>(({
  onFinish,
  onError,
  hidden,
  path,
  multiple,
  accept,
  project,
  onProgress,
  type,
  tmp = false,
}, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(async (file: File) => {
    try {
      const { url: putUrl, readUrl } = await mediaService.mediaServiceGetPresignedURL({
        projectId: project.id as number,
        payload: tmp
          ? { tmp: true }
          : { name: file.name, path, type },
      });

      if (!putUrl) {
        throw new Error("Failed to get presigned URL");
      }

      const response = await fetch(putUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "Access-Control-Allow-Origin": "*",
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const s3Response: S3Response = {
        filename: file.name,
        fileKey: "",
        signedUrl: putUrl,
        publicUrl: tmp && readUrl ? readUrl : putUrl.split("?").shift() || "",
        ...(tmp && readUrl && { readUrl }),
      };

      onFinish(s3Response, file);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      onError?.(errorMessage);
    }
  }, [project.id, path, type, tmp, onFinish, onError]);

  // Expose uploadFile method through ref
  useImperativeHandle(ref, () => ({
    uploadFile
  }), [uploadFile]);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (multiple) {
      // Handle multiple files
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (onProgress) {
          onProgress(0, "starting", file);
        }
        await uploadFile(file);
        if (onProgress) {
          onProgress(100, "finished", file);
        }
      }
    } else {
      // Handle single file
      const file = files[0];
      if (onProgress) {
        onProgress(0, "starting", file);
      }
      await uploadFile(file);
      if (onProgress) {
        onProgress(100, "finished", file);
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [multiple, onProgress, uploadFile]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      {!hidden && (
        <button
          type="button"
          onClick={handleClick}
          style={{
            padding: "8px 16px",
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          {multiple ? "Select Files" : "Select File"}
        </button>
      )}
    </>
  );
});

Uploader.displayName = 'Uploader';

export default Uploader;
